import { useCallback, useEffect, useMemo } from 'react'

import { MessageSequenceStatus } from '@api'
import { useUnit } from 'effector-react'

import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { $creatorId } from '@/models/creators'
import { RebillMessagesSkeleton } from '@/modules/creator-settings/rebill-messages/components/table-skeleton.tsx'
import { buildColumns } from '@/modules/creator-settings/rebill-messages/components/tables/columns.tsx'
import { compareTimes } from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import { useDeleteRebillMessageMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-delete-rebill-message.ts'
import { useToggleRebillMessageByMessageIdMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-toggle-rebill-message-toggle-by-messag-id.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

export const RebillMessagesTable = () => {
  const creatorId = useUnit($creatorId)
  const { isFetching, rebillMessages } = useSettingsContext()
  const deleteRebillMessageMutation = useDeleteRebillMessageMutation()
  const deleteRebillMessage = async (messageId: number) => {
    return deleteRebillMessageMutation.mutateAsync(String(messageId))
  }

  const enableRebillMessageToggleByMessageIdMutation =
    useToggleRebillMessageByMessageIdMutation()
  const handleToggleChange = async (id: number, value: boolean) => {
    return enableRebillMessageToggleByMessageIdMutation.mutateAsync({
      messageId: String(id),
      value: value
        ? MessageSequenceStatus.ACTIVE
        : MessageSequenceStatus.INACTIVE,
    })
  }

  const renderEmpty = useCallback(
    () => (
      <EmptyTableContent>
        It looks like you don&#39;t have any messages created yet, click on the{' '}
        <span className="text-pointer text-primary-500">Add Message</span>{' '}
        button to do so.
      </EmptyTableContent>
    ),
    [],
  )

  const columns = useMemo(
    () =>
      buildColumns({
        deleteMessage: deleteRebillMessage,
        toggleMessage: handleToggleChange,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const tableRebillData = useMemo(
    () =>
      rebillMessages.sort((a, b) =>
        compareTimes(a.timeframeStart!, b.timeframeStart!),
      ),
    [rebillMessages],
  )

  useEffect(() => {
    if (
      rebillMessages.some(
        (message) => message.sequence?.status === MessageSequenceStatus.ACTIVE,
      )
    ) {
      trackFeatureUsage(TrackedFeatures.RebillMessages, creatorId)
    }
  }, [rebillMessages, creatorId])

  if (isFetching) {
    return <RebillMessagesSkeleton />
  }

  return (
    <EmptyProvider render={renderEmpty}>
      <DataTable
        hasActionColumn
        columns={columns}
        data={tableRebillData}
        tbodyMinHeight={false}
      />
    </EmptyProvider>
  )
}
